/**
 * Server codes → catalogue sentences, shared by every block (01 §13): errors, reasons, tags, stay ranges,
 * contact lines and nearby-date suggestions. Values go through i18n (Intl, data-no-translation).
 */
import { h } from './dom.js';
import { trackError } from './analytics.js';

/**
 * @param {Object} i18n
 * @param {{phone: string, phoneFormatted?: string, email: string}|null} pageContact Contact from the page payload
 *   (admin settings): used when the API is unreachable.
 */
export function createMessages(i18n, pageContact) {
	const t = i18n.t;

	/** Template with tel: / mailto: links, else its _NO_CONTACT form. */
	function withContact(key, c = pageContact) {
		if (c && c.phone && c.email) {
			return t(key, [
				i18n.link('tel:' + c.phone.replace(/[^+\d]/g, ''), c.phoneFormatted || c.phone),
				i18n.link('mailto:' + c.email, c.email)
			]);
		}
		return t(key + '_NO_CONTACT');
	}

	/**
	 * SERVICE_UNAVAILABLE (or an upstream 502/503/504 page) and RATE_LIMITED (or a plain 429) with the contact;
	 * a known code → its template; anything else → the generic message.
	 */
	function errorFor(response) {
		trackError(response); // Matomo `erreur` (1.6.0): once per response, never one with its own event
		const code = response?.body?.error?.code;
		if (code === 'SERVICE_UNAVAILABLE' || (!code && [502, 503, 504].includes(response?.status))) {
			return withContact('error.SERVICE_UNAVAILABLE');
		}
		if (code === 'RATE_LIMITED' || (!code && response?.status === 429)) {
			return withContact('error.RATE_LIMITED');
		}
		if (code === 'RATE_SUSPENDED') {
			return withContact('error.RATE_SUSPENDED');
		}
		return t(code && i18n.has('error.' + code) ? 'error.' + code : 'error.GENERIC');
	}

	/** Reason / summary {code, …values} → sentence. */
	function reason(ns, obj) {
		const code = obj?.code || 'UNKNOWN';
		switch (code) {
			case 'NIGHT_TAKEN':
			case 'ROOM_TAKEN':
				return t(ns + '.' + code, [i18n.date(obj.night, 'day')]);
			case 'NO_CHECKIN':
			case 'NO_CHECKOUT':
				return t(ns + '.' + code, [i18n.date(obj.date, 'day')]);
			case 'MIN_STAY':
			case 'MAX_STAY':
				return t(ns + '.' + code, [obj.nights], obj.nights);
			case 'RATE_LEAD_TIME':
				return t(ns + '.' + code, [obj.days], obj.days);
			case 'CAPACITY':
				return t(ns + '.' + code, [obj.people], obj.people);
			case 'RATE_SUSPENDED':
				return withContact(ns + '.' + code);
			default:
				return t(i18n.has(ns + '.' + code) ? ns + '.' + code : ns + '.UNKNOWN');
		}
	}

	/** The contact phone as a tel: link (page contact, admin settings), or null when none is known. */
	function phoneLink(c = pageContact) {
		return c && c.phone ? i18n.link('tel:' + c.phone.replace(/[^+\d]/g, ''), c.phoneFormatted || c.phone) : null;
	}

	/** Capacity hint of the guest selector, from /config party.hints (server-side). */
	function partyHint(hint) {
		switch (hint?.code) {
			case 'ONE_ROOM':
				return t('party.ONE_ROOM', [hint.maxPerRoom], hint.maxPerRoom);
			case 'MIN_ROOMS':
				return t('party.MIN_ROOMS', [hint.party, hint.minRooms]);
			case 'NOT_ENOUGH_BEDS':
				return t('party.NOT_ENOUGH_BEDS', [hint.party, hint.beds]);
			default:
				return null;
		}
	}

	function stayRange(arrival, departure, nights) {
		return h('span', {}, t('ui.range', [i18n.date(arrival, 'short'), i18n.date(departure, 'long')]), i18n.sep(' · '), t('ui.nights', [nights], nights));
	}

	/** "Contactez-nous : phone · email" with tel: / mailto: links. */
	function contactLine(c) {
		if (!c || (!c.phone && !c.email)) {
			return null;
		}
		return h('p', { class: 'pap-contact' }, t('ui.contact', [
			i18n.link('tel:' + (c.phone || '').replace(/[^+\d]/g, ''), c.phoneFormatted || c.phone || ''),
			i18n.link('mailto:' + (c.email || ''), c.email || '')
		]));
	}

	/** Nearby dates proposed by the server; onPick(arrival, departure, type) — type for Matomo `suggestion` (1.8.0). */
	function suggestions(list, onPick) {
		if (!list || list.length === 0) {
			return null;
		}
		return h('div', { class: 'pap-suggestions' },
			h('p', {}, t('ui.suggestionsTitle')),
			...list.map((s) => h('button', { type: 'button', class: 'pap-btn pap-btn-ghost', on: { click: () => onPick(s.arrival, s.departure, s.type) } }, stayRange(s.arrival, s.departure, s.nights)))
		);
	}

	return { withContact, errorFor, reason, partyHint, stayRange, contactLine, suggestions, phoneLink };
}
